import { Field } from 'formik';
import { COOKIE_AUTH_DETAILS, ROLES } from 'globalconfig/constants/keys';
import { getCookie } from 'globalservices/cookie';
import { phoneNumberValidation } from 'globalutils/fmUtils';
import 'rc-tooltip/assets/bootstrap.css';
import React, { lazy, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { checkServiceProvider } from './serviceProviderConfig';

const layout = window.$environment?.CURRENT_LAYOUT;
const Input = lazy(() => import(`globalcomponents/UI/Input/${layout}`));
const Label = lazy(() => import(`globalcomponents/UI/FormLabel/${layout}`));
const Message = lazy(() => import(`globalcomponents/UI/Message/${layout}`));
const Button = lazy(() => import(`globalcomponents/UI/Button/${layout}`));
const SubHeader = lazy(() => import(`globalcomponents/SubHeader/${layout}`));
const Select = lazy(() => import(`globalcomponents/UI/Select/${layout}`));
const FormStyle = require(`globalcomponents/UI/Form/${layout}/serverStyle`).default;
const CheckBoxToggle = lazy(() => import(`globalcomponents/UI/Checkbox/${layout}`));
const Modal = require(`globalcomponents/UI/Modal/${layout}`);

const RightForm = ({ values, handleEdit, errors, isNotValid, Styled, hasSaveApiStatus, states, webfleetProviders }) => {
  const token = getCookie(COOKIE_AUTH_DETAILS);
  const SERVICE_PROVIDER = window.$environment?.SERVICE_PROVIDER;
  const i18n = useTranslation();

  const [cModalData, setCModalData] = useState(null);

  const showError = (field, validationErrors, isNotValid, apiError) => {
    if (validationErrors[field] && !isNotValid) {
      return <Message type="error" message={errors[field]} />;
    }
    if (apiError?.[field]) {
      return <Message type="error" message={apiError[field]} />;
    }
  };

  // CLUTCH ONLY
  const featureEnable = (featureName) => ({
    header: '',
    content: () => (
      <div className="deleteParent">
        <h2 style={{ textAlign: 'center' }}>{featureName} enabled</h2>
        <div className="deletePopupBtn">
          <div className="cancelBtn" />
          <div className="deletebtn">
            <Button
              label={i18n.t('common.ok')}
              onClick={() => {
                setCModalData(null);
              }}
            ></Button>
          </div>
        </div>
      </div>
    ),
  });

  // CLUTCH ONLY
  const featureDisable = (featureName) => ({
    header: '',
    content: () => (
      <div className="deleteParent">
        <h2 style={{ textAlign: 'center' }}>{featureName} disabled</h2>
        <div className="deletePopupBtn">
          <div className="cancelBtn" />
          <div className="deletebtn">
            <Button
              label={i18n.t('common.ok')}
              onClick={() => {
                setCModalData(null);
              }}
            ></Button>
          </div>
        </div>
      </div>
    ),
  });

  return (
    <Styled.rightSection>
      <div className="companyForm">
        <FormStyle.FieldsWrapper>
          <Label htmlFor="address" variant="body1">
            {i18n.t('common.address')}
          </Label>
          <FormStyle.TextWrapper>
            <Field
              name="address"
              id="address"
              handleOnChange={(event) => handleEdit(event, { field: 'address' })}
              as={Input}
              placeholder={i18n.t('common.address')}
              value={values['address']}
              type="text"
              disabled={checkServiceProvider('disabled_field', SERVICE_PROVIDER, false)}
            />
            {showError('address', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>
        <FormStyle.FieldsWrapper>
          <Label htmlFor="city" variant="body1">
            {i18n.t('common.city')}
          </Label>
          <FormStyle.TextWrapper>
            <Field
              name="city"
              id="city"
              handleOnChange={(event) => handleEdit(event, { field: 'city' })}
              as={Input}
              placeholder={i18n.t('common.city')}
              value={values['city']}
              type="text"
              disabled={checkServiceProvider('disabled_field', SERVICE_PROVIDER, false)}
            />
            {showError('city', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>

        <FormStyle.FieldsWrapper>
          <Label htmlFor="state" variant="body1">
            {i18n.t('common.state')}
          </Label>
          <FormStyle.TextWrapper>
            <Field
              name="state"
              id="state"
              onChange={(event) => {
                handleEdit(event, { field: 'state' });
              }}
              as={Select}
              placeholderText={i18n.t('common.state')}
              value={values['state']}
              suggestions={states}
              disabled={checkServiceProvider('disabled_field', SERVICE_PROVIDER, false)}
            />
            {showError('state', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>
        <FormStyle.FieldsWrapper>
          <Label htmlFor="zipCode" variant="body1">
            {i18n.t('common.zipCode')}
          </Label>
          <FormStyle.TextWrapper>
            <Field
              name="zipCode"
              id="zipCode"
              handleOnChange={(event) => handleEdit(event, { field: 'zipCode' })}
              as={Input}
              placeholder={i18n.t('common.zipCode')}
              value={values['zipCode']}
              type="text"
            />
            {showError('zipCode', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>
        <FormStyle.FieldsWrapper>
          <Label htmlFor="companyPhone" variant="body1">
            {i18n.t('common.phone')}
          </Label>
          <FormStyle.TextWrapper>
            <Field
              name="companyPhone"
              id="companyPhone"
              handleOnChange={(event) => handleEdit(event, { field: 'companyPhone' })}
              as={Input}
              placeholder={i18n.t('common.phone')}
              value={values['companyPhone']}
              type="text"
              updateValue={phoneNumberValidation}
            />
            {showError('companyPhone', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
          </FormStyle.TextWrapper>
        </FormStyle.FieldsWrapper>
        {checkServiceProvider('disabled_field', SERVICE_PROVIDER, false) && (
          <FormStyle.FieldsWrapper>
            <Label htmlFor="email" variant="body1">
              {i18n.t('common.email')}
            </Label>
            <FormStyle.TextWrapper>
              <Field
                name="email"
                id="email"
                handleOnChange={(event) => handleEdit(event, { field: 'email' })}
                as={Input}
                disabled
                placeholder={i18n.t('common.email')}
                value={values['email']}
                type="text"
              />
              <div className="helperText">{i18n.t('Email used to send Missing Information')}</div>
              {showError('email', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
            </FormStyle.TextWrapper>
          </FormStyle.FieldsWrapper>
        )}
        {checkServiceProvider('rightform_fields', SERVICE_PROVIDER, false) ? (
          <>
            {' '}
            <FormStyle.FieldsWrapper>
              <Label htmlFor="webfleetProvider" variant="body1">
                {i18n.t('webfleetProvider')}
              </Label>
              <FormStyle.TextWrapper>
                <Field
                  name="webfleetProvider"
                  id="webfleetProvider"
                  onChange={(event) => {
                    handleEdit(event, { field: 'webfleetProvider' });
                  }}
                  as={Select}
                  placeholderText={i18n.t('webfleetProvider')}
                  value={values['webfleetProvider']}
                  suggestions={webfleetProviders}
                  disabled={checkServiceProvider('disabled_field', SERVICE_PROVIDER, false)}
                />
                {showError('webfleetProvider', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
              </FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>
            <FormStyle.FieldsWrapper>
              <Label htmlFor="subscriptionTier" variant="body1">
                {i18n.t('registration.subscriptionTier')}
              </Label>
              <FormStyle.TextWrapper>
                <Field
                  name="subscriptionTier"
                  id="subscriptionTier"
                  handleOnChange={(event) => handleEdit(event, { field: 'subscriptionTier' })}
                  as={Input}
                  disabled
                  placeholder={i18n.t('registration.subscriptionTier')}
                  value={values['subscriptionTier']}
                  type="text"
                />
                {showError('subscriptionTier', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
              </FormStyle.TextWrapper>
            </FormStyle.FieldsWrapper>
          </>
        ) : null}
      </div>
      {!token?.roles?.includes(ROLES.SERVICE_MANAGER) && (
        <div className="companyForm">
          <FormStyle.FieldsWrapper>
            <Label htmlFor="contactPerson" variant="body1">
              {i18n.t('companyData.contactPerson')}
            </Label>
            <FormStyle.TextWrapper>
              <Field
                name="contactPerson"
                id="contactPerson"
                handleOnChange={(event) => handleEdit(event, { field: 'contactPerson' })}
                as={Input}
                placeholder={i18n.t('companyData.contactPerson')}
                value={values['contactPerson']}
                type="text"
              />
              {showError('contactPerson', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
            </FormStyle.TextWrapper>
          </FormStyle.FieldsWrapper>

          <FormStyle.FieldsWrapper>
            <Label htmlFor="otherLocationContactPerson" variant="body1">
              {i18n.t('companyData.otherLocationContactPerson')}
            </Label>
            <FormStyle.TextWrapper>
              <Field
                name="otherLocationContactPerson"
                id="otherLocationContactPerson"
                handleOnChange={(event) => handleEdit(event, { field: 'otherLocationContactPerson' })}
                as={Input}
                placeholder={i18n.t('companyData.otherLocationContactPerson')}
                value={values['otherLocationContactPerson']}
                type="text"
              />
              {showError('otherLocationContactPerson', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
            </FormStyle.TextWrapper>
          </FormStyle.FieldsWrapper>

          <FormStyle.FieldsWrapper>
            <Label htmlFor="otherLocationAddress" variant="body1">
              {i18n.t('companyData.otherLocationAddress')}
            </Label>
            <FormStyle.TextWrapper>
              <Field
                name="otherLocationAddress"
                id="otherLocationAddress"
                handleOnChange={(event) => handleEdit(event, { field: 'otherLocationAddress' })}
                as={Input}
                placeholder={i18n.t('companyData.otherLocationAddress')}
                value={values['otherLocationAddress']}
                type="text"
              />
              {showError('otherLocationAddress', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
            </FormStyle.TextWrapper>
          </FormStyle.FieldsWrapper>

          <FormStyle.FieldsWrapper>
            <Label htmlFor="otherLocationPhone" variant="body1">
              {i18n.t('companyData.otherLocationPhoneNumber')}
            </Label>
            <FormStyle.TextWrapper>
              <Field
                name="otherLocationPhone"
                id="otherLocationPhone"
                handleOnChange={(event) => handleEdit(event, { field: 'otherLocationPhone' })}
                as={Input}
                updateValue={phoneNumberValidation}
                placeholder={i18n.t('companyData.otherLocationPhoneNumber')}
                value={values['otherLocationPhone']}
                type="text"
              />
              {showError('otherLocationPhone', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
            </FormStyle.TextWrapper>
          </FormStyle.FieldsWrapper>

          <FormStyle.FieldsWrapper>
            <Label htmlFor="website" variant="body1">
              {i18n.t('companyData.website')}
            </Label>
            <FormStyle.TextWrapper>
              <Field
                name="website"
                id="website"
                handleOnChange={(event) => handleEdit(event, { field: 'website' })}
                as={Input}
                placeholder={i18n.t('companyData.website')}
                value={values['website']}
                type="text"
              />
              {showError('website', errors, isNotValid, hasSaveApiStatus?.errorMessage)}
            </FormStyle.TextWrapper>
          </FormStyle.FieldsWrapper>
          {checkServiceProvider('rightform_fields_settings', SERVICE_PROVIDER, false) && (
            <div>
              <SubHeader text={i18n.t('companyData.advanceSettings')} className="subheaderSection companyTitle" variant="h2" />
              <FormStyle.FieldsWrapper htmlFor="skipVehicleFilter" className="agreeTerms">
                <FormStyle.TextWrapper>
                  <CheckBoxToggle
                    aria-labelledby="agree"
                    type="NormalGrey"
                    checked={values.isDvirEnabled}
                    label={
                      i18n.t('companyData.DVIR') +
                      `\u00A0\u00A0  ${
                        values.clutchSubscriptionPlan === 'Monthly'
                          ? '(Monthly - 5\u00A0 dollar/truck)'
                          : '(Yearly - 48 dollar/truck)'
                      }`
                    }
                    onClick={(event) => {
                      handleEdit(event, {
                        field: 'isDvirEnabled',
                      });
                      if (event) {
                        setCModalData(featureEnable(i18n.t('companyData.DVIR')));
                      } else {
                        setCModalData(featureDisable(i18n.t('companyData.DVIR')));
                      }
                    }}
                    name="isDvirEnabled"
                  />
                </FormStyle.TextWrapper>
              </FormStyle.FieldsWrapper>

              <FormStyle.FieldsWrapper htmlFor="skipVehicleFilter" className="agreeTerms">
                <FormStyle.TextWrapper>
                  <CheckBoxToggle
                    aria-labelledby="agree"
                    type="NormalGrey"
                    checked={values.isIftaEnabled}
                    label={
                      i18n.t('companyData.Ifta') +
                      `\u00A0\u00A0  ${
                        values.clutchSubscriptionPlan === 'Monthly'
                          ? '(Monthly - 5\u00A0 dollar/truck)'
                          : '(Yearly - 48 dollar/truck)'
                      }`
                    }
                    onClick={(event) => {
                      handleEdit(event, {
                        field: 'isIftaEnabled',
                      });
                      if (event) {
                        setCModalData(featureEnable(i18n.t('companyData.Ifta')));
                      } else {
                        setCModalData(featureDisable(i18n.t('companyData.Ifta')));
                      }
                    }}
                    name="isIftaEnabled"
                  />
                </FormStyle.TextWrapper>
              </FormStyle.FieldsWrapper>
            </div>
          )}
        </div>
      )}
      {checkServiceProvider('rightform_cModal', SERVICE_PROVIDER, false) && cModalData && (
        <Modal.default open={!!cModalData} setOpen={() => setCModalData(null)} data={cModalData} alignItemsLeft companyData />
      )}
    </Styled.rightSection>
  );
};

export default RightForm;
